import { useState} from "react";

import { useProfileSetup } from "../../../contexts/ProfileContext";
import { useAccountSetup } from "../../../contexts/AccountSetupContext";
import { useEchoMail } from "../../../contexts/EchoMailContext";
import { useAuth } from "../../../contexts/AuthContext";

import {categories } from "../../channel-cats/ChannelCategories";

export default function OnboardingModal() {
  const { connections } = useAccountSetup();
  const { profileData, profileLabels } = useProfileSetup();
  const { domainInfo } = useEchoMail();
  const { user } = useAuth();

  // Filter out coming-soon channels
  const availableChannels = categories.flatMap(category => category.channels).filter(channel => channel.status !== "coming-soon");

  const channels = Object.fromEntries(
    availableChannels.map(channel => [channel.key, connections[channel.key]])
  );

  const [cardOpen, setCardOpen] = useState(false);
  const [openStep, setOpenStep] = useState(null);
  const [openCategory, setOpenCategory] = useState(null);

  const steps = [
    {
      title: "Channel connections",
      type: "channels",
    },
    {
      title: "Verify email domain",
      type: "domain",
    },
    {
      title: "Complete profile",
      type: "profile",
    },
  ];

  const isStepComplete = (type) => {
    if (type === "channels") {
      return Object.values(channels).every(Boolean);
    }
    if (type === "domain") {
      if (domainInfo == null) return false;
      return domainInfo.verified === true;
    }
    if (type === "profile") {
      return Object.values(profileData).every(Boolean);
    }
    return false;
  };

  const allStepsComplete = steps.every((step) => isStepComplete(step.type));

  return (
    <>
      {!allStepsComplete && user && (
        <div className="w-[205px] max-w-md rounded-xl border border-gray-300 bg-white shadow-md overflow-hidden z-50">
          <button
            onClick={() => setCardOpen(!cardOpen)}
            className="flex w-full items-center justify-between px-3 py-2 font-semibold text-gray-900 cursor-pointer hover:bg-gray-100"
          >
            <span>Getting started</span>
            <span className="text-gray-400">{cardOpen ? "▾" : "▸"}</span>
          </button>

          {cardOpen && (
            <div className="divide-y divide-gray-200">
              {steps.map((step, index) => (
                <div key={step.type}>
                  <div
                    onClick={() => setOpenStep(openStep === index ? null : index)}
                    className="flex items-center gap-3 px-6 py-3 cursor-pointer transition-colors duration-200"
                  >
                    <span className={`flex-1 text-sm font-medium ${isStepComplete(step.type) ? 'line-through text-black-500' : 'text-gray-900'}`}>
                      {step.title}
                    </span>
                    <span className="text-gray-400">
                      {openStep === index ? "▾" : "▸"}
                    </span>
                  </div>

                  {openStep === index && (
                    <div className="px-6 pb-4 pl-12 text-sm text-gray-600 space-y-2">
                      {/* Channels with nested categories */}
                      {step.type === "channels" &&
                        categories.map((category, catIndex) => {
                          const availableCategoryChannels = category.channels.filter(
                            channel => channel.status !== "coming-soon"
                          );

                          if (availableCategoryChannels.length === 0) return null;

                          return (
                            <div key={category.name} className="space-y-1">
                              <div
                                onClick={() => setOpenCategory(openCategory === catIndex ? null : catIndex)}
                                className="flex items-center justify-between cursor-pointer py-1"
                              >
                                <span className={`font-medium ${availableCategoryChannels.every(channel => connections[channel.key]) ? 'line-through text-gray-500' : 'text-gray-900'}`}>{category.name}</span>
                                <span className="text-gray-400 text-xs">
                                  {openCategory === catIndex ? "▾" : "▸"}
                                </span>
                              </div>

                              {openCategory === catIndex && (
                                <div className="pl-4 space-y-1">
                                  {availableCategoryChannels.map((channel) => (
                                    <label
                                      key={channel.key}
                                      className="flex items-center gap-2"
                                    >
                                      {channel.icon}
                                      <span className={`${connections[channel.key] ? 'line-through text-black-500' : 'text-gray-900'}`}>
                                        {channel.name}
                                      </span>
                                    </label>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}

                      {/* Domain */}
                      {step.type === "domain" && (
                        <p className={`${domainInfo?.verified ? 'line-through text-black-500' : 'text-gray-600'}`}>
                          {domainInfo?.verified ? "Verified" : "Not verified"}
                        </p>
                      )}

                      {/* Profile */}
                      {step.type === "profile" &&
                        Object.entries(profileData).map(([key, value]) => (
                          <p key={key} className={`${value ? 'line-through text-black-500' : 'text-gray-600'}`}>
                            {profileLabels[key] ?? key}
                          </p>
                        ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </>
  );
}